import App from './App.vue'
import { createApp, defineCustomElement } from 'vue'
import router from './router'
import vuetify from './plugins/vuetify'
import VueColumnsResizableVuetify from 'vue-columns-resizable-vuetify'
import VueGridLayout from 'vue-grid-layout'
import { setupOidcModule, vuexStore } from '@/stores/vuex/index'
import AnalyzeSearchCeVue from './components/Inputs/AnalyzeSearch.ce.vue'
import LoadMorePillCeVue from './components/Inputs/LoadMorePill.ce.vue'
import { FactoryManager } from './services/FactoryManager'

import '@fontsource/montserrat/300.css'
import '@fontsource/montserrat/500.css'
import '@fontsource/roboto/100.css'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import '@fontsource/roboto/900.css'

import { useEnvStore } from '@/stores/envStore'
import AuthorizationInterceptor, {
  AUTHZ_INTERCEPTOR_KEY,
} from './components/Authentication/services/authorization-interceptor'
import StatusCodeInterceptor, {
  STATUS_CODE_INTERCEPTOR_KEY,
} from './components/Authentication/services/status-code-interceptor'
import LoadingInterceptor, {
  LOADING_INTERCEPTOR_KEY,
} from './components/Authentication/services/loading-interceptor'
import { ServiceKeys } from './services/symbols'
import { pinia } from './stores'

const envStore = useEnvStore(pinia)
await envStore.fetchEnv()

setupOidcModule()

const factoryManager = new FactoryManager(
  envStore.variables.ANALYZE_API_BACKEND_URL,
  envStore.variables.ANALYZE_PLATFORM_URL,
)
const projectService = factoryManager.createProjectService()
const snapshotService = factoryManager.createSnapshotService()
const artifactService = factoryManager.createArtifactService()
const globalFilterService = factoryManager.createGlobalFilterService()

const app = createApp(App)
app
  .provide(ServiceKeys.globalFilter, globalFilterService)
  .provide(ServiceKeys.snapshot, snapshotService)
  .provide(ServiceKeys.artifact, artifactService)
  .provide(ServiceKeys.project, projectService)
  .provide(ServiceKeys.upload, factoryManager.createUploadService())
  .provide(ServiceKeys.customMetrics, factoryManager.createCustomMetricService())
  .provide(ServiceKeys.providerIcon, factoryManager.createProviderIconService())
  .provide(ServiceKeys.search, factoryManager.createSearchService())
  .provide(ServiceKeys.dashboard, factoryManager.createDashboardService())
  .provide(ServiceKeys.colorConverter, factoryManager.createColorCoverterService())
  .provide(ServiceKeys.chart, factoryManager.createChartService())
  .provide(ServiceKeys.rappidContext, factoryManager.createRappidContext())
  .provide(AUTHZ_INTERCEPTOR_KEY, new AuthorizationInterceptor())
  .provide(STATUS_CODE_INTERCEPTOR_KEY, new StatusCodeInterceptor())
  .provide(LOADING_INTERCEPTOR_KEY, new LoadingInterceptor())
  .provide(ServiceKeys.globalFiltersToolbox, factoryManager.createGlobalFilterToolboxService())
  .use(VueColumnsResizableVuetify)
  .use(router)
  .use(pinia)
  .use(vuexStore)
  .use(vuetify)
  .use(VueGridLayout)
  .mount('#app')

const syncStoreAndRouteService = factoryManager.createSyncStoreAndRouteService(
  projectService,
  snapshotService,
  artifactService,
  globalFilterService,
)

app.provide(ServiceKeys.sync, syncStoreAndRouteService)

// Currently, Vuetify cannot be used inside components that are registered as custom elements.
// This seems to be a limitation of Vuetify: https://github.com/vuetifyjs/vuetify/issues/5054
// Therefore, this custom element component aims to somewhat replicate the styles of AnalyzeSearch for use inside
// JointJS components. Once this issue is addressed, the AnalyzeSearchCE component will not need to manually rebuild the
// search input to look similar to what Vuetify provides.
customElements.define('analyze-search-ce', defineCustomElement(AnalyzeSearchCeVue))
customElements.define('load-more-pill-ce', defineCustomElement(LoadMorePillCeVue))
